import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

export const subscribeNewsletter = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) =>
    z
      .object({
        email: z.string().email().max(320),
        source: z.string().max(80).optional(),
      })
      .parse(data),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const email = data.email.toLowerCase().trim();

    // Upsert-style: try insert, if unique conflict, check existing state.
    const { data: existing } = await supabaseAdmin
      .from("newsletter_subscribers")
      .select("id, unsubscribe_token, unsubscribed_at, welcome_sent_at")
      .eq("email", email)
      .maybeSingle();

    if (existing) {
      // If previously unsubscribed, re-subscribe.
      if (existing.unsubscribed_at) {
        await supabaseAdmin
          .from("newsletter_subscribers")
          .update({ unsubscribed_at: null, confirmed_at: new Date().toISOString() })
          .eq("id", existing.id);
        // Send welcome again since they're returning.
        await sendWelcomeSafe(email, existing.unsubscribe_token, existing.id);
        return { ok: true, alreadySubscribed: false, resubscribed: true };
      }
      return { ok: true, alreadySubscribed: true };
    }

    const { data: inserted, error } = await supabaseAdmin
      .from("newsletter_subscribers")
      .insert({
        email,
        source: data.source ?? null,
        confirmed_at: new Date().toISOString(),
      })
      .select("id, unsubscribe_token")
      .single();

    if (error || !inserted) {
      throw new Error("Could not subscribe right now. Please try again.");
    }

    await sendWelcomeSafe(email, inserted.unsubscribe_token, inserted.id);
    return { ok: true, alreadySubscribed: false };
  });

async function sendWelcomeSafe(email: string, token: string | null, id: string) {
  if (!token) return;
  try {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { sendWelcomeEmail } = await import("@/lib/email/newsletter-emails.server");
    const { data: latest } = await supabaseAdmin
      .from("articles")
      .select("slug, title")
      .eq("status", "published")
      .order("published_at", { ascending: false })
      .limit(3);
    // Cap inline send at 4s so the subscribe response stays snappy;
    // the send-welcome cron sweep retries anything that times out or fails.
    const res = await Promise.race([
      sendWelcomeEmail({ email, token, latest: latest ?? [] }),
      new Promise<{ ok: false }>((resolve) => setTimeout(() => resolve({ ok: false }), 4000)),
    ]);
    if (res.ok) {
      await supabaseAdmin
        .from("newsletter_subscribers")
        .update({ welcome_sent_at: new Date().toISOString() })
        .eq("id", id);
    }
  } catch {
    // Cron sweep (send-welcome hook) will retry.
  }
}
